import { FormEvent, useContext } from 'react';
import { useTranslation } from 'react-i18next';
import { isEmailValid } from 'dash-common';
import {
  Form,
  Input,
  Button,
  useFormState,
  useFormValidation,
  FormErrorTypes,
  AlertsContext,
  Alerts,
  useServerResponseTranslation,
} from 'dash-ui';
import { useForgotPasswordMutation } from 'gql/generated.graphql';

enum FormFields {
  Email = 'email',
}

const FormFieldsList = [FormFields.Email];

function ForgotPasswordForm() {
  const { t } = useTranslation('forms');
  const { toggleAlert } = useContext(AlertsContext);
  const [formErrors, , [setEmailError]] = useFormValidation(FormFieldsList);
  const translateErrorMessage = useServerResponseTranslation();

  const [formState, [setEmail], [formOnSubmit, formOnError, formOnSuccess]] =
    useFormState(FormFieldsList);
  const [forgotPasswordMutation, { loading }] = useForgotPasswordMutation();

  const validEmail = (value: string) => {
    if (!value) {
      setEmailError({
        type: FormErrorTypes.Info,
        message: t`messages.emailRequired`,
      });
      return false;
    }
    if (!isEmailValid(value)) {
      setEmailError({
        type: FormErrorTypes.Invalid,
        message: t`messages.emailInvalid`,
      });
      return false;
    }
    setEmailError(null);
    return true;
  };

  const onEmailTextChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setEmail(e.target.value);
    validEmail(e.target.value);
  };

  const onSubmit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    event.stopPropagation();
    formOnSubmit();
    const { email } = formState;
    if (!validEmail(email)) {
      return null;
    }
    return forgotPasswordMutation({
      variables: { email },
    })
      .then(() => {
        formOnSuccess();
        toggleAlert(Alerts.ResetPasswordEmailsSent, true);
      })
      .catch((error) => {
        formOnError(translateErrorMessage(error));
        setEmailError({
          message: '',
          type: FormErrorTypes.Invalid,
        });
      });
  };

  return (
    <Form onSubmit={onSubmit} w={334}>
      <Form.Group mt={1}>
        <Form.Label>{t`labels.email`}</Form.Label>
        <Input
          w="100%"
          type="email"
          placeholder={t`placeholders.email` || ''}
          onChange={onEmailTextChange}
          $errorType={formErrors[FormFields.Email]?.type}
        />
        <Form.Feedback $errorType={formErrors[FormFields.Email]?.type}>
          {formErrors[FormFields.Email]?.message}
        </Form.Feedback>
      </Form.Group>
      <Form.Group mt={4}>
        <Button w="100%" type="submit" disabled={loading} isLoading={loading}>
          {t`labels.sendResetInstructions`}
        </Button>
      </Form.Group>
      <Form.Group mt={4}>
        <Form.Feedback $errorType={FormErrorTypes.Invalid}>
          {formState.errorSlug}
        </Form.Feedback>
      </Form.Group>
    </Form>
  );
}

export default ForgotPasswordForm;
